import { Column } from '@urc-ui/store/api/report-metadata/types';
import { REPORT_CATEGORY } from '@urc-ui/utils/types';

/**
 * Maps last-touch column IDs to a *template* for the MTA column ID. The
 * template uses the `{L}` placeholder to mark where the lookback token
 * (e.g., `7d`, `14d`, `30d`) is substituted at lookup time. This separates
 * "which semantic metric" from "which lookback window," so new lookback
 * windows (e.g., 30d, 60d) can be supported without touching the per-category
 * mapping tables — just by adding to LOOKBACK_TOKENS.
 *
 * Templates handle all three MTA column ID naming styles:
 *   - Carbyne (underscore lookback):    'mta_sales_{L}'       → 'mta_sales_14d'
 *   - OfflineAPI (camelCase lookback):  'multiTouchSales{L}'  → 'multiTouchSales14d'
 *   - OfflineAPI with modifier:         'multiTouchSales{L}CoV' → 'multiTouchSales14dCoV'
 *   - OfflineAPI without lookback:      'multiTouchSalesClicks' (no placeholder; matched as-is)
 */
const MTA_SP_MAPPING: Map<string, string> = new Map<string, string>([
  // Carbyne last-touch → MTA template (Carbyne style: underscore-prefixed lookback)
  ['total_sales_14d', 'mta_sales_{L}'],
  ['total_sales_from_clicks_7d', 'mta_sales_{L}'],
  ['total_sales_from_clicks_14d', 'mta_sales_{L}'],
  ['purchase_click_count_7d', 'mta_purchase_{L}'],
  ['purchase_click_count_14d', 'mta_purchase_{L}'],
  ['purchase_count_14d', 'mta_purchase_{L}'],
  ['total_units_sold_14d', 'mta_units_sold_{L}'],
  ['total_units_sold_from_clicks_7d', 'mta_units_sold_{L}'],
  ['total_units_sold_from_clicks_14d', 'mta_units_sold_{L}'],
  ['total_acos_14d', 'mta_acos_{L}'],
  ['total_roas_14d', 'mta_roas_{L}'],
  ['total_acos_from_clicks_7d', 'mta_acos_{L}'],
  ['total_roas_from_clicks_7d', 'mta_roas_{L}'],
  ['total_acos_from_clicks_14d', 'mta_acos_{L}'],
  ['total_roas_from_clicks_14d', 'mta_roas_{L}'],
  ['purchase_click_rate_7d', 'mta_purchase_rate_over_clicks_{L}'],
  ['purchase_click_rate_14d', 'mta_purchase_rate_over_clicks_{L}'],
  ['same_sku_sales_from_clicks_7d', 'mta_sales_promoted_{L}'],
  ['same_sku_sales_from_clicks_14d', 'mta_sales_promoted_{L}'],
  ['same_sku_units_sold_from_clicks_7d', 'mta_units_sold_promoted_{L}'],
  ['same_sku_units_sold_from_clicks_14d', 'mta_units_sold_promoted_{L}'],
  ['other_sku_sales_from_clicks_7d', 'mta_sales_halo_{L}'],
  ['other_sku_sales_from_clicks_14d', 'mta_sales_halo_{L}'],
  ['other_sku_units_sold_from_clicks_7d', 'mta_units_sold_halo_{L}'],
  ['other_sku_units_sold_from_clicks_14d', 'mta_units_sold_halo_{L}'],
  // OfflineAPI last-touch → MTA template (camelCase lookback, with modifiers after lookback)
  ['sales7d', 'multiTouchSales{L}'],
  ['sales7dCoV', 'multiTouchSales{L}CoV'],
  ['sales14d', 'multiTouchSales{L}'],
  ['sales14dCoV', 'multiTouchSales{L}CoV'],
  ['purchases7d', 'multiTouchPurchases{L}'],
  ['purchases14d', 'multiTouchPurchases{L}'],
  ['purchaseClickRate7d', 'multiTouchPurchaseRateOverClicks{L}'],
  ['purchaseClickRate14d', 'multiTouchPurchaseRateOverClicks{L}'],
  ['unitsSoldClicks7d', 'multiTouchUnitsSold{L}'],
  ['unitsSoldClicks14d', 'multiTouchUnitsSold{L}'],
  ['acos7d', 'multiTouchAcos{L}'],
  ['roas7d', 'multiTouchRoas{L}'],
  ['acos14d', 'multiTouchAcos{L}'],
  ['roas14d', 'multiTouchRoas{L}'],
  ['acosClicks7d', 'multiTouchAcos{L}'],
  ['acosClicks14d', 'multiTouchAcos{L}'],
  ['roasClicks7d', 'multiTouchRoas{L}'],
  ['roasClicks14d', 'multiTouchRoas{L}'],
  ['salesOtherSku7d', 'multiTouchSalesOtherSku{L}'],
  ['salesOtherSku7dCoV', 'multiTouchSalesOtherSku{L}CoV'],
  ['purchasesOtherSku7d', 'multiTouchPurchasesOtherSku{L}'],
  ['unitsSoldSameSku7d', 'multiTouchUnitsSoldSameSku{L}'],
  ['unitsSoldOtherSku7d', 'multiTouchUnitsSoldOtherSku{L}'],
  ['attributedSalesSameSku7d', 'multiTouchSalesSameSku{L}'],
  ['attributedSalesSameSku7dCoV', 'multiTouchSalesSameSku{L}CoV']
]);

const MTA_SB_SD_MAPPING: Map<string, string> = new Map<string, string>([
  // Carbyne last-touch → MTA template (underscore-prefixed lookback)
  ['sales_14d', 'mta_sales_{L}'],
  ['total_sales_14d', 'mta_sales_{L}'],
  ['total_sales_from_clicks_14d', 'mta_sales_click_{L}'],
  ['orders_14d', 'mta_purchase_{L}'],
  ['total_orders_14d', 'mta_purchase_{L}'],
  ['purchase_count_14d', 'mta_purchase_{L}'],
  ['units_sold_14d', 'mta_units_sold_{L}'],
  ['total_units_sold_14d', 'mta_units_sold_{L}'],
  ['total_units_sold_from_clicks_14d', 'mta_units_sold_click_{L}'],
  ['total_acos_14d', 'mta_acos_{L}'],
  ['total_roas_14d', 'mta_roas_{L}'],
  ['total_acos_from_clicks_14d', 'mta_acos_click_{L}'],
  ['total_roas_from_clicks_14d', 'mta_roas_click_{L}'],
  ['purchase_click_rate_14d', 'mta_purchase_rate_over_clicks_{L}'],
  ['purchase_click_count_14d', 'mta_purchase_click_{L}'],
  ['other_sku_purchase_click_count_14d', 'mta_purchase_halo_{L}'],
  ['other_sku_sales_from_clicks_14d', 'mta_sales_halo_{L}'],
  ['other_sku_units_sold_from_clicks_14d', 'mta_units_sold_halo_{L}'],
  // OfflineAPI last-touch → MTA column (SB/SD OfflineAPI MTA IDs have no lookback suffix)
  ['sales14d', 'multiTouchSalesClicks'],
  ['totalSales14d', 'multiTouchSales'],
  ['totalSales14dCoV', 'multiTouchSalesCoV'],
  ['sales14dCoV', 'multiTouchSalesClicksCoV'],
  ['purchases14d', 'multiTouchPurchasesClicks'],
  ['purchaseClickRate14d', 'multiTouchPurchaseRateOverClicks'],
  ['purchaseCount14d', 'multiTouchPurchases'],
  ['totalUnitsSold14d', 'multiTouchUnitsSold'],
  ['unitsSoldClicks14d', 'multiTouchUnitsSoldClicks'],
  ['totalAcos14d', 'multiTouchAcos'],
  ['acosClicks14d', 'multiTouchAcosClicks'],
  ['totalRoas14d', 'multiTouchRoas'],
  ['roasClicks14d', 'multiTouchRoasClicks']
]);

const REPORT_CATEGORY_TO_MTA_MAPPING: Map<REPORT_CATEGORY, Map<string, string>> = new Map<REPORT_CATEGORY, Map<string, string>>([
  [REPORT_CATEGORY.SP, MTA_SP_MAPPING],
  [REPORT_CATEGORY.SD, MTA_SB_SD_MAPPING],
  [REPORT_CATEGORY.SB, MTA_SB_SD_MAPPING]
]);

/**
 * Lookback tokens tried when substituting `{L}` in an MTA column template.
 * Order matters: the lookback extracted from the last-touch column is tried
 * first (to preserve same-lookback pairing); any remaining tokens are tried in
 * this preference order as fallbacks.
 *
 * To support a new lookback window (e.g., 60d), add its token here.
 */
const LOOKBACK_TOKENS: readonly string[] = ['30d', '14d', '7d'];

/**
 * Matches any lookback token (e.g., '7d', '14d', '30d') in a column ID. The
 * last match in the string is used, which handles both end-of-string placement
 * (e.g., 'total_sales_7d') and mid-string placement before modifiers like
 * `CoV` (e.g., 'sales7dCoV').
 */
const LOOKBACK_PATTERN = /(\d+d)/g;

const LOOKBACK_PLACEHOLDER = '{L}';

export interface MergeMtaColumnsRequest {
  readonly reportCategory: REPORT_CATEGORY;
  readonly lastTouchColumns: Column[];
  readonly mtaColumns: Column[];
}

/**
 * Merges MTA columns next to their last-touch counterpart. For each last-touch
 * column, resolves its corresponding MTA column via a lookback-agnostic lookup
 * and, if found in the provided MTA column list, inserts it immediately after
 * the last-touch column in the output.
 */
export function mergeMtaColumns(request: MergeMtaColumnsRequest) {
  const mtaColumnMap: Map<string, Column> = new Map<string, Column>();
  const mergedList: Column[] = [];

  request.mtaColumns.forEach(mtaColumn => {
    mtaColumnMap.set(mtaColumn.id, mtaColumn);
  });

  request.lastTouchColumns.forEach(column => {
    mergedList.push(column);
    const resolvedMtaColumn = resolveMtaColumn(request.reportCategory, column.id, mtaColumnMap);
    if (resolvedMtaColumn) {
      mergedList.push(resolvedMtaColumn);
    }
  });

  return mergedList;
}

function resolveMtaColumn(reportCategory: REPORT_CATEGORY, lastTouchColumn: string, mtaColumnMap: Map<string, Column>): Column | undefined {
  const template = REPORT_CATEGORY_TO_MTA_MAPPING.get(reportCategory)?.get(lastTouchColumn);
  if (!template) return undefined;

  // Template with no {L} placeholder: match MTA column ID exactly (SB/SD OfflineAPI case).
  if (!template.includes(LOOKBACK_PLACEHOLDER)) {
    return mtaColumnMap.get(template);
  }

  // Prefer the last-touch column's own lookback to preserve same-lookback pairing.
  const preferred = extractLookback(lastTouchColumn);
  const orderedTokens = preferred ? [preferred, ...LOOKBACK_TOKENS.filter(t => t !== preferred)] : [...LOOKBACK_TOKENS];

  for (const token of orderedTokens) {
    const candidateId = template.replace(LOOKBACK_PLACEHOLDER, token);
    const column = mtaColumnMap.get(candidateId);
    if (column) return column;
  }
  return undefined;
}

function extractLookback(columnId: string): string | undefined {
  const matches = columnId.match(LOOKBACK_PATTERN);
  return matches ? matches[matches.length - 1] : undefined;
}
